import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ConfigProvider } from "antd";
import { XIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { cn } from "shadcn/lib/utils";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-1000 grid place-items-center overflow-y-auto bg-black/50 py-4",
        className,
      )}
      {...props}
    />
  );
}

// Where a popup opened from `trigger` should render. A modal dialog makes the
// rest of the page unclickable and closes on clicks outside itself, so a popup
// drawn in document.body (antd selects and date pickers, hand-made dropdowns,
// antd confirm dialogs) has to go inside the dialog instead.
function getPopupHost(trigger?: Element | null) {
  return (
    trigger?.closest<HTMLElement>('[data-slot="dialog-content"]') ??
    document.body
  );
}

// The element focus should go back to when a dialog closes: whatever had focus
// as it opened. When that is an item in a menu, the item disappears with the
// menu, so use the button that opened the menu (Radix labels a menu with its
// trigger), going up through submenus.
function getOpener() {
  let opener = document.activeElement;
  for (
    let menu = opener?.closest("[role=menu]");
    menu;
    menu = opener?.closest("[role=menu]")
  ) {
    const trigger = document.getElementById(
      menu.getAttribute("aria-labelledby") ?? "",
    );
    if (!trigger) break;
    opener = trigger;
  }
  return opener;
}

// Radix only returns focus to a DialogTrigger, and most dialogs here open from
// state. This mounts with the dialog content and finds the opener while
// rendering, before a field inside the dialog takes focus with autoFocus.
function RememberOpener({
  into,
}: {
  into: React.MutableRefObject<Element | null>;
}) {
  const [opener] = useState(getOpener);
  useEffect(() => {
    into.current = opener;
  }, [into, opener]);
  return null;
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onCloseClick,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  onCloseClick?: () => void;
}) {
  const returnFocusRef = useRef<Element | null>(null);
  return (
    <DialogPortal data-slot="dialog-portal">
      {/* The dialog sits inside the backdrop, so a dialog taller than the
          window scrolls with the backdrop instead of being cut off. */}
      <DialogOverlay>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-lg border bg-background p-6 shadow-lg duration-200 outline-none",
            className,
          )}
          {...props}
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event);
            if (event.defaultPrevented) return;
            event.preventDefault();
            const target = returnFocusRef.current;
            if (target instanceof HTMLElement) target.focus();
          }}
          onEscapeKeyDown={(event) => {
            // Escape in an antd confirm opened from this dialog, or with an antd
            // select or date picker open inside it, closes only that. Remove
            // together with antd.
            const target =
              event.target instanceof Element ? event.target : null;
            const openAntdPopup = target
              ?.closest('[data-slot="dialog-content"]')
              ?.querySelector(
                ".ant-select-dropdown:not(.ant-select-dropdown-hidden), .ant-picker-dropdown:not(.ant-picker-dropdown-hidden)",
              );
            if (target?.closest(".ant-modal-root") || openAntdPopup) {
              event.preventDefault();
            }
            onEscapeKeyDown?.(event);
          }}
          onInteractOutside={(event) => {
            // A click on a toast, or on the backdrop's scrollbar, is not a
            // click outside the dialog.
            const { target } = event;
            const { originalEvent } = event.detail;
            const onScrollbar =
              target instanceof HTMLElement &&
              target.dataset.slot === "dialog-overlay" &&
              originalEvent instanceof MouseEvent &&
              originalEvent.clientX >= target.clientWidth;
            if (
              onScrollbar ||
              (target instanceof Element &&
                target.closest(".ant-notification, .Toastify"))
            ) {
              event.preventDefault();
            }
            onInteractOutside?.(event);
          }}
        >
          <RememberOpener into={returnFocusRef} />
          {/* Remove the ConfigProvider together with antd. */}
          <ConfigProvider getPopupContainer={getPopupHost}>
            {children}
          </ConfigProvider>
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className="absolute top-4 right-4 z-10 cursor-pointer rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              onClick={onCloseClick}
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  getPopupHost,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
};
